import { numberToHexColor } from "../../utils/fillSelection";
import { findNcsColorByHex } from "./findNcsColorByHex";
import type { NcsColorOption } from "./ncsColors";

/** Cap so the pinned row stays scannable above the full NCS grid. */
export const USED_NCS_COLORS_MAX = 12;

/**
 * Default plan fill (`0xffffff`). Every unpainted wall resolves to this via
 * `getFillColor`, so pinning it would drown out intentional NCS choices.
 */
const DEFAULT_EXCLUDED_HEXES = new Set(["#FFFFFF"]);

export type CollectUsedNcsColorsOptions = {
  /** Numeric `fillColor` values from walls / fill-capable symbols. */
  fillColors: Iterable<number | undefined | null>;
  /** Extra hex strings (e.g. ceiling color) already applied in the plan. */
  extraHexes?: Iterable<string | undefined | null>;
  /** Hexes to skip (uppercase `#RRGGBB`). Defaults to pure white. */
  excludeHexes?: ReadonlySet<string>;
  max?: number;
};

/**
 * Resolves unique in-plan fill hexes to NCS palette entries (first hex match).
 * Non-NCS fills and excluded defaults are dropped. Order follows first sighting.
 */
export function collectUsedNcsColors(
  options: CollectUsedNcsColorsOptions,
): NcsColorOption[] {
  const {
    fillColors,
    extraHexes,
    excludeHexes = DEFAULT_EXCLUDED_HEXES,
    max = USED_NCS_COLORS_MAX,
  } = options;

  const seenHexes = new Set<string>();
  const uniqueHexes: string[] = [];

  const considerHex = (raw: string | undefined | null) => {
    if (!raw) {
      return;
    }
    const hex = raw.trim().toUpperCase();
    if (!hex || excludeHexes.has(hex) || seenHexes.has(hex)) {
      return;
    }
    seenHexes.add(hex);
    uniqueHexes.push(hex);
  };

  for (const color of fillColors) {
    if (color === undefined || color === null) {
      continue;
    }
    considerHex(numberToHexColor(color));
  }

  if (extraHexes) {
    for (const hex of extraHexes) {
      considerHex(hex);
    }
  }

  const seenNames = new Set<string>();
  const result: NcsColorOption[] = [];

  for (const hex of uniqueHexes) {
    if (result.length >= max) {
      break;
    }
    const option = findNcsColorByHex(hex);
    if (!option || seenNames.has(option.name)) {
      continue;
    }
    seenNames.add(option.name);
    result.push(option);
  }

  return result;
}
